/*
 * Copyright (c) 2026. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */
import type { UINotification } from '@poweralpha/common';
import { type ReactNode, Suspense, useMemo, useState } from 'react';
import { DesktopShellRouter } from '../desktop/navigation/DesktopShellRouter';
import { lazy } from '../lazy';
import {
  NotificationCenterContext,
  type NotificationCenterContextValue,
} from './NotificationCenterContext';

const NotificationCenterDialog = lazy(() => import('./dialog/NotificationCenterDialog'));

interface DialogState {
  open: boolean;
  /** Stays true after the first open so the dialog can animate out. */
  mounted: boolean;
  notification?: UINotification;
}

/** Owns the notification center dialog above the window layer, like the agent panel. */
export function NotificationCenterProvider({ children }: Readonly<{ children: ReactNode }>) {
  const [state, setState] = useState<DialogState>({ open: false, mounted: false });

  const value = useMemo<NotificationCenterContextValue>(
    () => ({
      openNotificationCenter: (notification) =>
        setState({ open: true, mounted: true, notification }),
      closeNotificationCenter: () => setState((prev) => ({ ...prev, open: false })),
    }),
    [],
  );

  return (
    <NotificationCenterContext.Provider value={value}>
      {children}
      {state.mounted && (
        <DesktopShellRouter>
          <Suspense fallback={null}>
            <NotificationCenterDialog
              open={state.open}
              initialNotification={state.notification}
              onClose={value.closeNotificationCenter}
            />
          </Suspense>
        </DesktopShellRouter>
      )}
    </NotificationCenterContext.Provider>
  );
}
